import { fmtTol } from "@/lib/utils";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flame, Plus, Loader2, Flag, ChevronRight, ArrowRight } from "lucide-react";
import {
  listSlittingPlans, getSlittingPlan, createSlittingPlan, editSlittingPlan,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import SlittingPlanForm from "@/components/SlittingPlanForm";
import PlanDetail from "@/components/SlittingPlanDetail";
import { DraggablePlanList } from "@/components/DraggablePlanList";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "completed", label: "Completed" },
  { key: "urgent", label: "🚩 Urgent" },
];

// P1 darkest → last priority lightest (rank 0..count-1 among numbered plans in view).
const priorityShade = (rank, count) => {
  const t = count > 1 ? rank / (count - 1) : 0;
  const light = 14 + t * 70; // 14% → 84% lightness
  return { backgroundColor: `hsl(222 47% ${light}%)`, color: light < 55 ? "#fff" : "#0f172a" };
};

const PlanRow = ({ plan, onOpen, rank = 0, rankCount = 1 }) => {
  const boundCount = plan.lines.filter((l) => l.mother_coil_id).length;
  const totalLines = plan.lines.length;
  const isCompleted = plan.status === "completed";
  const allBound = boundCount === totalLines;
  const slitText = (l) => {
    const slits = (l.slit_plan || []).filter((s) => s.slit_width_mm && s.count);
    if (!slits.length) return "";
    const used = slits.reduce((a, s) => a + s.slit_width_mm * s.count, 0);
    const trim = +(Number(l.target_width_mm || 0) - used).toFixed(2);
    return ` · ${slits.map((s) => `${+s.slit_width_mm}X${s.count}`).join(", ")}${l.target_width_mm ? ` (T=${trim} mm)` : ""}`;
  };
  const summary = plan.lines
    .map((l) => `${l.target_grade} ${l.target_thickness_mm}mm · Tol ${fmtTol(l.tolerance_mm)} · ${l.num_parts || 1} part${(l.num_parts || 1) !== 1 ? "s" : ""}${slitText(l)}`)
    .join(" + ");

  return (
    <button
      data-testid={`plan-row-${plan.plan_number}`}
      onClick={onOpen}
      className="w-full text-left bg-white border border-slate-200 rounded-sm px-4 py-3 hover:border-slate-900 hover:shadow-sm transition-all flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {!isCompleted && <div
          data-testid={`plan-priority-no-${plan.plan_number}`}
          title="Priority number (lowest coil in this plan)"
          style={plan.min_priority_no ? priorityShade(rank, rankCount) : undefined}
          className={`h-10 w-10 shrink-0 rounded-sm flex flex-col items-center justify-center font-mono transition-colors ${plan.min_priority_no ? "" : "border border-dashed border-slate-300 text-slate-400"}`}
        >
          <span className="text-[8px] tracking-wider uppercase leading-none">P</span>
          <span className="text-sm font-bold leading-tight">{plan.min_priority_no || "—"}</span>
        </div>}
        <div className="font-heading text-lg font-bold text-slate-900 font-mono tracking-tight w-20 shrink-0">{plan.plan_number}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm ${
              isCompleted ? "bg-green-100 text-green-800 border border-green-200"
                : allBound ? "bg-blue-100 text-blue-800 border border-blue-200"
                  : "bg-amber-100 text-amber-800 border border-amber-200"
            }`}>
              {isCompleted ? "Completed" : allBound ? "All RMs scanned" : `${boundCount}/${totalLines} scanned`}
            </span>
            {plan.priority === "urgent" && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white">
                <Flag className="h-3 w-3" /> Urgent
              </span>
            )}
            <span className="text-xs text-slate-500">· {totalLines} RM{totalLines !== 1 ? "s" : ""}</span>
          </div>
          <p data-testid={`plan-summary-${plan.plan_number}`} className="text-sm text-slate-700 mt-1 break-words">{summary}</p>
        </div>
        <div className="text-right shrink-0 hidden sm:block">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Created</p>
          <p className="font-mono text-xs text-slate-900 mt-0.5">{(plan.created_at || "").slice(0, 10)}</p>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
    </button>
  );
};

const SlittingDashboard = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("pending");
  const [createOpen, setCreateOpen] = useState(false);
  const [openPlanId, setOpenPlanId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const params = useMemo(() => {
    if (filter === "all") return {};
    if (filter === "urgent") return { priority: "urgent" };
    return { status: filter };
  }, [filter]);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["slitting-plans", params],
    queryFn: () => listSlittingPlans(params),
  });
  const priorityRanks = [...new Set(plans.map((p) => p.min_priority_no).filter(Boolean))].sort((a, b) => a - b);

  const { data: openPlan } = useQuery({
    queryKey: ["slitting-plan", openPlanId],
    queryFn: () => getSlittingPlan(openPlanId),
    enabled: !!openPlanId,
    refetchInterval: openPlanId ? 4000 : false,
  });

  const createMut = useMutation({
    mutationFn: createSlittingPlan,
    onSuccess: (res) => {
      toast.success(`Plan ${res.plan_number} created · ${res.sfgs?.length || 0} SFG codes issued. Scan the RM to unlock label printing.`);
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ["slitting-plans"] });
      // Auto-open detail so the user immediately sees the "scan RM" call-to-action.
      setOpenPlanId(res.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create plan"),
  });

  const editMut = useMutation({
    mutationFn: (payload) => editSlittingPlan(openPlanId, payload),
    onSuccess: () => {
      toast.success("Plan updated");
      setEditMode(false);
      qc.invalidateQueries({ queryKey: ["slitting-plans"] });
      qc.invalidateQueries({ queryKey: ["slitting-plan", openPlanId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Edit failed"),
  });

  return (
    <div data-testid="slitting-dashboard" className="space-y-5">
      <div className="border-b border-slate-200 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Flame className="h-7 w-7 text-slate-900" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Slitting Operations</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Plans dashboard</h1>
          </div>
        </div>
        <Button data-testid="new-plan-btn" onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-5">
          <Plus className="h-4 w-4 mr-1.5" /> New plan
        </Button>
      </div>

      <div className="flex justify-end">
        <Link
          to="/inventory?kind=sfg"
          data-testid="view-sfg-in-inventory"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-800 hover:text-orange-900 border border-orange-300 bg-orange-50 hover:bg-orange-100 rounded-sm px-3 py-2 transition-colors"
        >
          View all SFG strips in Inventory <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`h-9 px-3 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-colors ${
              filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      )}

      {!isLoading && plans.length === 0 && (
        <div data-testid="empty-state" className="bg-white border border-dashed border-slate-300 rounded-sm py-16 text-center">
          <p className="text-slate-500 text-sm">No plans in this view.</p>
          <Button onClick={() => setCreateOpen(true)} className="mt-3 bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Create first plan
          </Button>
        </div>
      )}

      {!isLoading && plans.length > 0 && filter === "pending" && (
        <>
          <p className="text-xs text-slate-500" data-testid="drag-hint">Drag a plan by its handle (or use ⤒ to send it to the top) to change priority. Coils are renumbered 1, 2, 3… in the new order.</p>
          <DraggablePlanList
            plans={plans}
            renderRow={(p) => (
              <PlanRow
                plan={p}
                rank={priorityRanks.indexOf(p.min_priority_no)} rankCount={priorityRanks.length}
                onOpen={() => { setOpenPlanId(p.id); setEditMode(false); }}
              />
            )}
          />
        </>
      )}

      {!isLoading && plans.length > 0 && filter !== "pending" && (
        <div data-testid="plans-list" className="space-y-2">
          {plans.map((p) => (
            <PlanRow
              key={p.id} plan={p}
              rank={priorityRanks.indexOf(p.min_priority_no)} rankCount={priorityRanks.length}
              onOpen={() => { setOpenPlanId(p.id); setEditMode(false); }}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-start justify-center p-4 overflow-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl mt-8 mb-8 p-5" data-testid="create-plan-modal">
            <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-3">
              <h2 className="font-heading text-xl font-bold text-slate-900">New slitting plan</h2>
              <button onClick={() => setCreateOpen(false)} className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
            </div>
            <SlittingPlanForm
              onCancel={() => setCreateOpen(false)}
              onSubmit={(payload) => createMut.mutate(payload)}
              submitting={createMut.isPending}
            />
          </div>
        </div>
      )}

      {/* Detail / Edit modal */}
      {openPlanId && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-start justify-center p-4 overflow-auto">
          <div className="w-full max-w-4xl mt-8 mb-8 relative">
            {!openPlan && (
              <div className="bg-white rounded-sm p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
            )}
            {openPlan && !editMode && (
              <PlanDetail
                plan={openPlan}
                onClose={() => setOpenPlanId(null)}
                onEdit={() => setEditMode(true)}
              />
            )}
            {openPlan && editMode && (
              <div className="bg-white rounded-sm p-5" data-testid="edit-plan-modal">
                <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-3">
                  <h2 className="font-heading text-xl font-bold text-slate-900">Edit {openPlan.plan_number}</h2>
                  <button onClick={() => setEditMode(false)} className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
                </div>
                <SlittingPlanForm
                  existingPlan={openPlan}
                  onCancel={() => setEditMode(false)}
                  onSubmit={(payload) => editMut.mutate(payload)}
                  submitting={editMut.isPending}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SlittingDashboard;
